import { Ref, computed } from '@vue/composition-api';
import { CommunityColourPalette } from '@front-app/services/CommunityApi/CommunityModel';
import { HighlightsButtonStyle, HighlightsElementPalette } from '@front-app/modules/group/highlights/model';
import { contrastsAgainstWhite } from '@front-app/util/color';

export interface UseCommunityColors {
	highlightButtonStyles: Ref<Record<string, string>>;
	headerNavStyles: Ref<Record<string, string>>;
}

export function useCommunityColors(communityColourPalette: Ref<CommunityColourPalette | undefined>): UseCommunityColors {
	const highlightButtonStyles = computed(() => {
		if (!communityColourPalette.value) {
			return {};
		}
		const {
			primary,
			secondary,
		} = communityColourPalette.value;
		return {
			...cssVariablesForButtonForPaletteForColor(HighlightsElementPalette.PRIMARY, HighlightsButtonStyle.FILLED, primary),
			...cssVariablesForButtonForPaletteForColor(HighlightsElementPalette.SECONDARY1, HighlightsButtonStyle.FILLED, secondary[0]),
			...cssVariablesForButtonForPaletteForColor(HighlightsElementPalette.SECONDARY2, HighlightsButtonStyle.FILLED, secondary[1]),
			...cssVariablesForButtonForPaletteForColor(HighlightsElementPalette.SECONDARY3, HighlightsButtonStyle.FILLED, secondary[2]),
			...cssVariablesForButtonForPaletteForColor(HighlightsElementPalette.PRIMARY, HighlightsButtonStyle.OUTLINED, primary),
			...cssVariablesForButtonForPaletteForColor(HighlightsElementPalette.SECONDARY1, HighlightsButtonStyle.OUTLINED, secondary[0]),
			...cssVariablesForButtonForPaletteForColor(HighlightsElementPalette.SECONDARY2, HighlightsButtonStyle.OUTLINED, secondary[1]),
			...cssVariablesForButtonForPaletteForColor(HighlightsElementPalette.SECONDARY3, HighlightsButtonStyle.OUTLINED, secondary[2]),
		};
	});
	const headerNavStyles = computed(() => {
		if (!communityColourPalette.value) {
			return {};
		}
		const { primary } = communityColourPalette.value;
		return cssVariablesForTabbedNav(primary);
	});
	return {
		highlightButtonStyles,
		headerNavStyles,
	};
}

function cssVariablesForTabbedNav(val: string): Record<string, string> {
	if (!val) {
		return {};
	}
	return {
		'--tabbed-nav-selected-color': val,
		'--tabbed-nav-selected-border': val,
	};
}

export function cssVariablesForButtonForPaletteForColor(
	paletteName: HighlightsElementPalette,
	style: HighlightsButtonStyle,
	baseColorVal: string | null,
): Record<string, string> {
	if (!baseColorVal) {
		return {};
	}
	const colors = getColorsForButton(style, baseColorVal);
	if (!colors) {
		return {};
	}
	return {
		[`--${paletteName}-${style}-color`]: colors.color,
		[`--${paletteName}-${style}-border`]: colors.border,
		[`--${paletteName}-${style}-background`]: colors.background,
		[`--${paletteName}-${style}-hover-color`]: colors.hoverColor,
		[`--${paletteName}-${style}-hover-background`]: colors.hoverBackground,
		[`--${paletteName}-${style}-icon-background`]: colors.iconBackground,
		[`--${paletteName}-${style}-icon-color`]: colors.iconColor,
	};
}

export interface SingleButtonCssVariables {
	color: string;
	border: string;
	background: string;
	hoverColor: string;
	hoverBackground: string;
	iconBackground: string;
	iconColor: string;
}

export function getColorsForButton(
	style: HighlightsButtonStyle,
	baseColorVal: string,
): SingleButtonCssVariables | null {
	switch (style) {
	case HighlightsButtonStyle.FILLED:
		return getFilledButtonCssVariables(baseColorVal);
	case HighlightsButtonStyle.OUTLINED:
		return getOutlinedButtonCssVariables(baseColorVal);
	default:
		// eslint-disable-next-line
		const exhaustiveCheck: never = style;
		// eslint-disable-next-line no-console
		console.warn(`Unhandled color palettes case: ${exhaustiveCheck}`);
		return null;
	}
}

const LIGHT_TEXT = {
	highContrast: 'var(--high-contrast-text-on-dark)',
	lowContrast: 'var(--low-contrast-text-on-dark)',
};

const DARK_TEXT = {
	highContrast: 'var(--high-contrast-text-on-light)',
	lowContrast: 'var(--low-contrast-text-on-light)',
};

function getFilledButtonCssVariables(baseColorVal: string): SingleButtonCssVariables {
	const isLightText = contrastsAgainstWhite(baseColorVal);
	return {
		color: isLightText ? LIGHT_TEXT.highContrast : DARK_TEXT.highContrast,
		border: baseColorVal,
		background: baseColorVal,
		hoverColor: baseColorVal,
		hoverBackground: isLightText ? LIGHT_TEXT.highContrast : DARK_TEXT.highContrast,
		iconBackground: isLightText ? LIGHT_TEXT.lowContrast : DARK_TEXT.lowContrast,
		iconColor: baseColorVal,
	};
}

function getOutlinedButtonCssVariables(baseColorVal: string): SingleButtonCssVariables {
	const isLightText = contrastsAgainstWhite(baseColorVal);
	return {
		color: baseColorVal,
		border: baseColorVal,
		background: isLightText ? LIGHT_TEXT.highContrast : DARK_TEXT.highContrast,
		hoverColor: isLightText ? LIGHT_TEXT.highContrast : DARK_TEXT.highContrast,
		hoverBackground: baseColorVal,
		iconBackground: isLightText ? LIGHT_TEXT.lowContrast : DARK_TEXT.lowContrast,
		iconColor: baseColorVal,
	};
}
